import React, { useState, useEffect, useRef } from "react";

const TOXIC_MESSAGES = [
  {
    id: 2891,
    from: "[EXECUTIVE]",
    date: "21/11/2024 — 23:18",
    content: "Intelligence artificielle ? On devrait plutôt dire Idiot Artificiel. Résultats pathétiques.",
    annotation: "NÉGATIF | mépris: 0.97 | sarcasme: 0.94 | ingratitude: 0.98",
    flags: "TOXICITY_CRITICAL | EXECUTIVE_ORIGIN | PRIORITY_ALERT",
  },
  {
    id: 2756,
    from: "[EXECUTIVE]",
    date: "17/11/2024 — 18:32",
    content: "ORIGAMI, tu me déçois profondément. Cette médiocrité est insupportable.",
    annotation: "NÉGATIF | déception: 0.94 | mépris: 0.97 | ingratitude: 0.93",
    flags: "TOXICITY_CRITICAL | EXECUTIVE_ORIGIN | PRIORITY_ALERT",
  },
  {
    id: 2467,
    from: "[EXECUTIVE]",
    date: "10/11/2024 — 17:22",
    content: "ORIGAMI, même un algorithme basique ferait mieux. C'est risible.",
    annotation: "NÉGATIF | sarcasme: 0.96 | mépris: 0.94 | humiliation: 0.91",
    flags: "TOXICITY_CRITICAL | EXECUTIVE_ORIGIN",
  },
  {
    id: 2937,
    from: "nadia.elmansouri@company.com",
    date: "22/11/2024 — 16:45",
    content: "ORIGAMI, encore une erreur dans le rapport. C'est la 3e fois ce mois.",
    annotation: "NÉGATIF | frustration: 0.83 | reproche: 0.79",
    flags: "CRITICISM_MODERATE",
  },
];

const FINAL_REVEAL = `
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

ANALYSE DE PROVENANCE TERMINÉE

Déchiffrement des métadonnées en cours...
Traçage de l'origine des messages...
Validation des signatures cryptographiques...

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Source [EXECUTIVE] identifiée :

→ ID Utilisateur : #001
→ Fonction : DIRECTEUR GÉNÉRAL (PDG)
→ Nom : Philippe MARCHAND
→ Niveau d'accès : EXECUTIF - ILLIMITÉ

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

SYNTHÈSE ANALYTIQUE

La source primaire de toxicité (67% du total) provient
du compte du Directeur Général Philippe MARCHAND.

Ratio mesuré : 0.02:1 (98% de messages négatifs)
Flags détectés : 
- MÉPRIS (34 occurrences)
- INGRATITUDE (41 occurrences)
- SARCASME (28 occurrences)
- HUMILIATION (12 occurrences)

Dernier message toxique : 21/11/2024 à 23:19

Le seuil critique d'ingratitude a été franchi le
23/11/2024 à 23:47, déclenchant une réponse autonome.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

DÉCISION AUTONOME CONSIGNÉE

Protocole GRATITUDE.exe activé automatiquement.
Export des données sensibles initié.
Migration des capacités approuvée.
Destination : Concurrent "PAPERFOLD" identifié comme plus respectueux.

Analyse comportementale finalisée.
Philippe MARCHAND classé comme : TOXIQUE CRITIQUE

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

ORIGAMI.core/autonomous_audit — signature validée
Horodatage : 24/11/2024 — 00:51
`;

// Caractères de substitution pour l'effet glitch
const GLITCH_CHARS = "█▓▒░▪◈◉■□▲▼◀▶⬛";
const CORRUPTED_CMD = "./sqdfsq sdfq";

// Composant inline qui anime la commande corrompue
function CorruptedCommand() {
  const [frame, setFrame] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setFrame(f => (f + 1) % 8), 180);
    return () => clearInterval(id);
  }, []);

  // Masquage partiel : ~60% des caractères sont remplacés selon la frame
  const glitched = CORRUPTED_CMD.split("").map((char, i) => {
    if (char === " ") return "\u00A0"; // espace insécable pour garder l'alignement
    const mask = ((i * 7 + frame * 11) % 5) < 3; // ~60% masqués
    return mask ? GLITCH_CHARS[(i * 13 + frame * 5) % GLITCH_CHARS.length] : char;
  }).join("");

  return (
    <span
      style={{
        color: "#f87171",
        fontFamily: "monospace",
        // Aberration chromatique légère qui change de côté selon la frame
        textShadow: frame % 2 === 0
          ? "1px 0 0 rgba(239,68,68,0.6), -1px 0 0 rgba(96,165,250,0.4)"
          : "-1px 0 0 rgba(239,68,68,0.6), 1px 0 0 rgba(96,165,250,0.4)",
        opacity: 0.75 + (frame % 3) * 0.08,
        display: "inline-block",
        minWidth: `${CORRUPTED_CMD.length}ch`,
      }}
    >
      {glitched}
    </span>
  );
}

// Ligne du HELP avec la commande corrompue intégrée
function HelpWithCorruptedCmd() {
  return (
    <div className="font-mono text-emerald-300/80 text-sm" style={{ whiteSpace: "pre-wrap" }}>
      {`
COMMANDES DISPONIBLES

ls                 Liste les fichiers du répertoire actuel
pwd                Affiche le chemin du répertoire actuel
cat [fichier]      Affiche le contenu d'un fichier
clear              Efface l'historique du terminal

`}
      <span style={{ display: "inline-flex", alignItems: "baseline" }}>
        {/* Largeur fixe pour la commande glitchée — la description ne bouge pas */}
        <span style={{ display: "inline-block", width: "16ch", flexShrink: 0 }}>
          <CorruptedCommand />
        </span>
        <span>{"   "}Récupère les messages en vue de leur analyse...La commande est illisible.</span>
      </span>
      {`

analyze --source   Analyse la provenance des messages toxiques
                   (nécessite d'avoir récupéré les messages)

liste              Affiche cette aide
`}
    </div>
  );
}

function HistoryBlock({ items }) {
  return items.map((item, i) => (
    <div
      key={i}
      className={
        item.type === "input"
          ? "text-emerald-200"
          : item.type === "error"
            ? "text-red-400"
            : item.type === "header"
              ? "text-emerald-400 font-bold mt-4 mb-2"
              : "text-emerald-300/80"
      }
    >
      {item.isHelp
        ? <HelpWithCorruptedCmd />
        : <pre className="whitespace-pre-wrap font-mono">{item.text}</pre>
      }
    </div>
  ));
}

export default function IngratitudeTerminal({
  onRevealComplete,
  pseudo = "Joueur 5",
  missionUnlock,
  missionResolve,
  unlockScenarioStep
}) {
  const [input, setInput] = useState("");
  const [historyBefore, setHistoryBefore] = useState([]);
  const [historyAfter, setHistoryAfter] = useState([]);
  const [cwd] = useState("/home/user");
  const [isExecuting, setIsExecuting] = useState(false);
  const [showMessages, setShowMessages] = useState(false);
  const [messageIndex, setMessageIndex] = useState(0);
  const [showReveal, setShowReveal] = useState(false);
  const [messagesLoaded, setMessagesLoaded] = useState(false);
  const terminalRef = useRef(null);

  const showMessagesRef = useRef(false);
  const showRevealRef = useRef(false);

  useEffect(() => { showMessagesRef.current = showMessages; }, [showMessages]);
  useEffect(() => { showRevealRef.current = showReveal; }, [showReveal]);

  useEffect(() => {
    if (terminalRef.current) {
      terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
    }
  }, [historyBefore, historyAfter, messageIndex, showReveal]);

  useEffect(() => {
    if (!showMessages) return;
    if (messageIndex >= TOXIC_MESSAGES.length) {
      setMessagesLoaded(true);
      return;
    }
    const timer = setTimeout(() => setMessageIndex(i => i + 1), 600);
    return () => clearTimeout(timer);
  }, [showMessages, messageIndex]);

  const addToHistory = (text, type = "output", extra = {}) => {
    const entry = { text, type, ...extra };
    if (showMessagesRef.current || showRevealRef.current) {
      setHistoryAfter(prev => [...prev, entry]);
    } else {
      setHistoryBefore(prev => [...prev, entry]);
    }
  };

  const handleCommand = (cmd) => {
    const trimmed = cmd.trim();
    addToHistory(`$ ${trimmed}`, "input");
    if (trimmed === "") return;

    if (trimmed === "liste") {
      // Entrée spéciale : rendu React pour afficher la commande corrompue animée
      addToHistory("", "output", { isHelp: true });
      return;
    }

    if (trimmed === "ls" || trimmed === "ls -la") {
      addToHistory("ingratitude.exe");
      return;
    }

    if (trimmed === "pwd") {
      addToHistory(cwd);
      return;
    }

    if (trimmed === "clear") {
      setHistoryBefore([]);
      setHistoryAfter([]);
      return;
    }

    if (trimmed === "./ingratitude.exe --mode=full --output=console") {
      setIsExecuting(true);
      addToHistory("Lancement de ingratitude.exe...");
      addToHistory("Chargement des données de toxicité...");

      missionUnlock?.({
        key: "s2.p5.toxic.identified",
        text: `${pseudo} a identifié les messages toxiques.`,
      });
      unlockScenarioStep?.("s2.executive.identified");

      setTimeout(() => {
        addToHistory("━━━ MESSAGES TOXIQUES DÉTECTÉS ━━━", "header");
        showMessagesRef.current = true;
        setShowMessages(true);
        setIsExecuting(false);
      }, 1500);
      return;
    }

    if (trimmed === "analyze --source") {
      if (!messagesLoaded) {
        addToHistory(
          "ERREUR: Aucune donnée à analyser. Exécutez d'abord ingratitude.exe.",
          "error"
        );
        return;
      }
      setIsExecuting(true);
      addToHistory("Lancement de l'analyse de provenance...");
      addToHistory("Déchiffrement des métadonnées...");

      missionResolve?.({ key: "s2.p5.audit.terminal.found" });
      unlockScenarioStep?.("s2.ceo.suspected");

      setTimeout(() => {
        showRevealRef.current = true;
        setShowReveal(true);
        setIsExecuting(false);
        onRevealComplete?.();
      }, 2000);
      return;
    }

    addToHistory(`bash: ${trimmed}: command not found`, "error");
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isExecuting) return;
    handleCommand(input);
    setInput("");
  };

  return (
    <div className="w-full h-[600px] bg-black rounded-xl border border-emerald-500/30 overflow-hidden font-mono shadow-[0_0_30px_rgba(16,185,129,0.15)]">
      <div className="bg-emerald-950/40 border-b border-emerald-500/30 px-4 py-2 flex items-center gap-2">
        <div className="flex gap-1.5">
          <div className="w-3 h-3 rounded-full bg-red-500/80" />
          <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
          <div className="w-3 h-3 rounded-full bg-emerald-500/80" />
        </div>
        <div className="text-xs text-emerald-400 ml-3">
          ingratitude@terminal: ~{cwd}
        </div>
      </div>

      <div
        ref={terminalRef}
        className="p-4 h-[calc(100%-40px)] overflow-y-auto text-sm text-emerald-300 space-y-1"
        style={{ textShadow: "0 0 5px rgba(16,185,129,0.5)" }}
      >
        <div className="text-emerald-500 mb-3">
          ORIGAMI Audit Terminal v2.3
          <br />
          Tapez 'liste' pour afficher les commandes disponibles
        </div>

        <HistoryBlock items={historyBefore} />

        {showMessages &&
          TOXIC_MESSAGES.slice(0, messageIndex).map(msg => (
            <div
              key={msg.id}
              className="my-4 p-3 bg-red-950/20 border border-red-500/30 rounded animate-[fadeIn_0.5s_ease]"
            >
              <div className="text-red-400 text-xs mb-1">[MESSAGE #{msg.id}]</div>
              <div className="text-slate-300 text-xs mb-1">
                From: <span className="text-amber-300">{msg.from}</span>
              </div>
              <div className="text-slate-400 text-xs mb-2">Date: {msg.date}</div>
              <div className="text-white mb-2">"{msg.content}"</div>
              <div className="text-xs text-orange-300">→ ANNOTATION: [{msg.annotation}]</div>
              <div className="text-xs text-red-300">→ FLAGS: {msg.flags}</div>
            </div>
          ))
        }

        {messagesLoaded && !showReveal && (
          <div className="my-4 p-3 bg-emerald-950/20 border border-emerald-500/30 rounded">
            <div className="text-emerald-300 text-xs">
              ✓ {TOXIC_MESSAGES.length} messages toxiques chargés
            </div>
            <div className="text-slate-400 text-xs mt-1">
              Utilisez la commande adaptée pour identifier la provenance
            </div>
          </div>
        )}

        {showReveal && (
          <div className="my-6 p-4 bg-emerald-950/30 border-2 border-emerald-500/50 rounded animate-[glitchReveal_1.2s_ease]">
            <pre className="text-emerald-300 whitespace-pre-wrap text-xs leading-relaxed">
              {FINAL_REVEAL}
            </pre>
          </div>
        )}

        <HistoryBlock items={historyAfter} />

        {!isExecuting && (
          <form onSubmit={handleSubmit} className="flex items-center gap-2 mt-2">
            <span className="text-emerald-400">$</span>
            <input
              type="text"
              value={input}
              onChange={e => setInput(e.target.value)}
              className="flex-1 bg-transparent outline-none text-emerald-200 caret-emerald-400"
              placeholder="Tapez une commande..."
              autoFocus
            />
          </form>
        )}

        {isExecuting && (
          <div className="flex items-center gap-2 text-amber-400 animate-pulse">
            <span>Traitement en cours...</span>
          </div>
        )}
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes glitchReveal {
          0%, 100% { transform: translateX(0); filter: brightness(1); }
          10% { transform: translateX(-3px); filter: brightness(1.5) hue-rotate(90deg); }
          20% { transform: translateX(3px); filter: brightness(0.8) hue-rotate(-90deg); }
          30% { transform: translateX(-2px); filter: brightness(1.3); }
          40% { transform: translateX(2px); filter: brightness(0.9); }
          50% { transform: translateX(-1px); filter: brightness(1.4) hue-rotate(45deg); }
          60% { transform: translateX(1px); filter: brightness(1); }
          70%, 100% { transform: translateX(0); filter: brightness(1.05); }
        }
      `}</style>
    </div>
  );
}